import type { opensearchtypes } from '@opensearch-project/opensearch';

type ErrorCause = opensearchtypes.ErrorCause;
type ErrorResponse = opensearchtypes.ErrorResponseBase;

const FALLBACK_MESSAGE = 'The search failed for an unknown reason';

const nonEmpty = (text: string | undefined): string | undefined => {
  const trimmed = text?.trim();

  return trimmed ? trimmed : undefined;
};

/**
 * The text a person can act on is rarely the top-level `reason`. A `search_phase_execution_exception`
 * leaves it empty and puts the real failure under `caused_by`; a shard failure says "all shards failed"
 * and puts the parse error under `root_cause` or `failed_shards`. Try the specific places before the
 * generic ones, and never return an empty string — the panel would render an empty alert.
 */
export const getOpensearchErrorMessage = (error: ErrorCause | string): string => {
  if (typeof error === 'string') return nonEmpty(error) ?? FALLBACK_MESSAGE;

  const [rootCause] = error.root_cause ?? [];
  const [failedShard] = error.failed_shards ?? [];
  const candidates = [
    error.caused_by?.reason,
    rootCause?.caused_by?.reason,
    rootCause?.reason,
    failedShard?.reason?.caused_by?.reason,
    failedShard?.reason?.reason,
    error.reason,
    error.type,
  ];

  return candidates.map(nonEmpty).find(Boolean) ?? FALLBACK_MESSAGE;
};

/**
 * A search can fail on SOME shards and still answer HTTP 200 carrying hits and no `error` envelope, so the
 * failure is invisible both to `toOpensearchSearchError` and to every caller that reads only `hits`. Returns
 * a one-line description when that happened, for callers that would rather log a degraded result than
 * present partial data as complete.
 */
export const getShardFailureMessage = (value: unknown): string | undefined => {
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle -- OpenSearch's own response field
  const { _shards: shards } = (value ?? {}) as { _shards?: opensearchtypes.ShardStatistics };

  if (!shards?.failed) return undefined;

  const [failure] = shards.failures ?? [];
  const reason = failure?.reason ? getOpensearchErrorMessage(failure.reason) : undefined;

  return `${shards.failed} of ${shards.total} shards failed${reason ? `: ${reason}` : ''}`;
};

/**
 * The `{ error }` envelope returned in place of search hits. `status` is deliberately not required: the
 * search endpoint answers 200 and carries it, but the gateway rejects an invalid search with an HTTP
 * status and a body that is nothing but `error`.
 */
export const isOpensearchErrorResponse = (value: unknown): value is ErrorResponse => {
  if (typeof value !== 'object' || value === null) return false;

  if (!('error' in value)) return false;

  const { error } = value;

  return typeof error === 'string' || (typeof error === 'object' && error !== null);
};

/**
 * A failed search as a real `Error`, so every consumer that reads `message` — panel alerts, tooltips, the
 * logger — gets the OpenSearch reason instead of `undefined`. The envelope stays attached for callers that
 * need the status or the exception type.
 */
export class OpensearchSearchError extends Error {
  readonly errorResponse: ErrorResponse;

  constructor(errorResponse: ErrorResponse) {
    super(getOpensearchErrorMessage(errorResponse.error));
    this.name = 'OpensearchSearchError';
    this.errorResponse = errorResponse;
  }
}

/**
 * Recovers the OpenSearch reason from wherever a failed search hid it: the search endpoint answers 200
 * and puts the envelope in `rawResponse`, while the gateway rejects an invalid search (a 400 for an
 * oversized aggregation) and leaves it in the HTTP response body, which no consumer reads — so the value
 * that reaches a panel has no `message` and the panel renders an empty red banner.
 *
 * Returns `undefined` when there is no envelope to find, leaving the value for the caller to pass on.
 */
export const toOpensearchSearchError = (value: unknown): OpensearchSearchError | undefined => {
  const { data, rawResponse, response } = (value ?? {}) as {
    data?: unknown;
    rawResponse?: unknown;
    response?: { data?: unknown };
  };
  const envelope = [value, rawResponse, data, response?.data].find(isOpensearchErrorResponse);

  return envelope ? new OpensearchSearchError(envelope) : undefined;
};
